import React from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { 
  PlatformExerciseDetailResponse, 
  getExerciseCategoryName, 
  getDifficultyLevelName 
} from './types';

interface InstructorPreviewModalProps {
  exercise: PlatformExerciseDetailResponse | null;
  isOpen: boolean;
  onClose: () => void;
}

export function InstructorPreviewModal({ exercise, isOpen, onClose }: InstructorPreviewModalProps) {
  if (!isOpen || !exercise) return null;

  const getDifficultyBadgeColor = (difficulty: number) => {
    switch (difficulty) {
      case 1: // Beginner
        return 'bg-green-100 text-green-800 border-green-200';
      case 2: // Intermediate
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 3: // Advanced
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 4: // Expert
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
        <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b">
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="text-2xl text-gray-900 mb-2">
                📱 Instructor Preview Mode
              </CardTitle>
              <p className="text-sm text-gray-600">
                This is how instructors will see this exercise in the mobile app
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={onClose}
              className="ml-4"
            >
              ✕ Close Preview
            </Button>
          </div>
        </CardHeader>
        
        <CardContent className="p-6">
          {/* Mobile App Mockup Container */}
          <div className="max-w-sm mx-auto bg-gray-900 rounded-[2.5rem] p-2 shadow-2xl">
            {/* Phone Frame */}
            <div className="bg-white rounded-[2rem] overflow-hidden">
              {/* Status Bar */}
              <div className="bg-gray-50 px-6 py-2 flex justify-between items-center text-xs">
                <span>9:41</span>
                <div className="flex items-center space-x-1">
                  <div className="w-4 h-2 bg-green-500 rounded-sm"></div>
                  <span>100%</span>
                </div>
              </div>
              
              {/* App Header */}
              <div className="bg-blue-600 text-white px-4 py-3">
                <div className="flex items-center justify-between">
                  <h1 className="text-lg font-semibold">CueFlow</h1>
                  <div className="text-sm">Session Mode</div>
                </div>
              </div>
              
              {/* Exercise Content */}
              <div className="p-4 min-h-[500px]">
                {/* Exercise Header */}
                <div className="mb-4">
                  <h2 className="text-xl font-bold text-gray-900 mb-2">
                    {exercise.name}
                  </h2>
                  
                  <div className="flex items-center space-x-2 mb-3">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getDifficultyBadgeColor(exercise.difficulty)}`}>
                      {getDifficultyLevelName(exercise.difficulty)}
                    </span>
                    <span className="text-sm text-gray-600">
                      {getExerciseCategoryName(exercise.category)}
                    </span>
                  </div>
                  
                  <div className="bg-blue-50 p-3 rounded-lg mb-4">
                    <div className="flex items-center mb-2">
                      <span className="text-lg">🏋️</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {exercise.primaryEquipmentName}
                      </span>
                    </div>
                    
                    {exercise.defaultSpringSettings && (
                      <div className="text-sm text-gray-700">
                        <strong>Springs:</strong> {exercise.defaultSpringSettings}
                      </div>
                    )}
                    
                    {exercise.commonPrimarySetups && (
                      <div className="text-sm text-gray-700">
                        <strong>Setup:</strong> {exercise.commonPrimarySetups}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Exercise Details */}
                {exercise.description && (
                  <div className="mb-4">
                    <h3 className="font-semibold text-gray-900 mb-2">Description</h3>
                    <p className="text-gray-700 text-sm leading-relaxed">
                      {exercise.description}
                    </p>
                  </div>
                )}
                
                {exercise.instructions && (
                  <div className="mb-4">
                    <h3 className="font-semibold text-gray-900 mb-2">Instructions</h3>
                    <div className="bg-yellow-50 p-3 rounded-lg">
                      <p className="text-gray-700 text-sm leading-relaxed">
                        {exercise.instructions}
                      </p>
                    </div>
                  </div>
                )}
                
                {/* Exercise Parameters */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  {exercise.defaultReps && (
                    <div className="bg-gray-50 p-3 rounded-lg text-center">
                      <div className="text-lg font-bold text-blue-600">
                        {exercise.defaultReps}
                      </div>
                      <div className="text-xs text-gray-600">Reps</div>
                    </div>
                  )}
                  
                  {exercise.typicalDurationSeconds && (
                    <div className="bg-gray-50 p-3 rounded-lg text-center">
                      <div className="text-lg font-bold text-blue-600">
                        {Math.round(exercise.typicalDurationSeconds / 60)}m
                      </div>
                      <div className="text-xs text-gray-600">Duration</div>
                    </div>
                  )}
                </div>
                
                {/* Alternative Equipment */}
                {exercise.alternativeEquipment && exercise.alternativeEquipment.length > 0 && (
                  <div className="mb-4">
                    <h3 className="font-semibold text-gray-900 mb-2">Alternative Equipment</h3>
                    <div className="space-y-2">
                      {exercise.alternativeEquipment.map((alt, index) => (
                        <div key={index} className="bg-gray-50 p-2 rounded text-sm">
                          <span className="font-medium">{alt.equipmentName}</span>
                          {alt.alternativeSpringSettings && (
                            <span className="text-gray-600 ml-2">
                              ({alt.alternativeSpringSettings})
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Tags */}
                {exercise.tags && (
                  <div className="mb-4">
                    <div className="flex flex-wrap gap-1">
                      {exercise.tags.split(',').map((tag, index) => (
                        <span 
                          key={index}
                          className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded"
                        >
                          {tag.trim()}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Action Buttons (as they would appear in the app) */}
                <div className="mt-6 space-y-2">
                  <Button className="w-full bg-green-600 hover:bg-green-700 text-white">
                    ✓ Mark Complete
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full border-yellow-400 text-yellow-700 hover:bg-yellow-50"
                  >
                    ⚠️ Mark Modified
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full border-gray-400 text-gray-700 hover:bg-gray-50"
                  >
                    ⏭ Skip Exercise
                  </Button>
                </div>
              </div>
            </div>
          </div>
          
          {/* Preview Notes */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <h4 className="font-semibold text-blue-900 mb-2">
              📋 Preview Notes for SuperAdmins
            </h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• This is how instructors will see this exercise during live sessions</li>
              <li>• The mobile interface is optimized for quick access to key information</li>
              <li>• Action buttons allow instructors to track exercise completion in real-time</li>
              <li>• All exercise data (springs, setup, instructions) is immediately accessible</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}